import {NewsletterSignupSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/order_success_page/v1/sections.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  useTrackFeature,
  useTrackFeatureInViewImpression,
} from '@aboutyou/tracking';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  IconSize,
  Image,
  InfoBox,
  desktopAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {NewsletterButton} from './NewsletterButton';
import {useSubscribeToNewsletter} from './hooks/useSubscribeToNewsletter';

interface NewsletterSectionProps {
  cbdParameter: string;
}

export const NewsletterSection: React.FC<
  NewsletterSignupSection & NewsletterSectionProps
> = ({cbdParameter, newsletterInfo, type}) => {
  const trackFeature = useTrackFeature();

  const subscribe = useSubscribeToNewsletter(cbdParameter);

  const alreadySubscribed = type?.$case === 'subscribed';

  const {t} = useTranslation();

  const onClick = () => {
    trackFeature({eventType: 'submit', label: 'newsletter_subscription'});

    return subscribe();
  };

  const {ref} = useTrackFeatureInViewImpression<
    HTMLButtonElement | HTMLDivElement
  >({
    label: alreadySubscribed
      ? 'existing_newsletter_subscriber_banner'
      : 'newsletter_subscription',
  });

  if (type?.$case === 'subscribed') {
    return (
      <NewsletterSectionStreamWrapper>
        <SuccessInfoBox
          ref={ref as React.RefObject<HTMLDivElement | null>}
          data-testid="newsletterPlacementAlreadySubscribed"
          type="success"
          text={type.subscribed.text}
        />
      </NewsletterSectionStreamWrapper>
    );
  }

  if (type?.$case === 'pending') {
    return (
      <NewsletterSectionStreamWrapper>
        <Wrapper data-testid="newsletterPlacement">
          <SubscribeSection>
            <Title>{newsletterInfo.headline}</Title>
            <PendingInfoBox
              ref={ref as React.RefObject<HTMLDivElement | null>}
              data-testid="newsletterPlacementAlreadySubscribed"
              type="success"
              text={type.pending.confirmText}
            />
          </SubscribeSection>
          <InfoSection>
            <Subtitle data-testid="USPTitle">
              {newsletterInfo.uspTitle}
            </Subtitle>
            <InfoItems>
              {newsletterInfo.usps.map((usp, index) => (
                <InfoItem key={index} data-testid="USPItem">
                  <StyledImage
                    src={usp.icon.url}
                    breakpoints={[]}
                    ratio={'1:1'}
                    alt={usp.icon.description}
                  />
                  <span>{usp.label}</span>
                </InfoItem>
              ))}
            </InfoItems>
          </InfoSection>
        </Wrapper>
      </NewsletterSectionStreamWrapper>
    );
  }

  if (type?.$case === 'notSubscribed') {
    return (
      <NewsletterSectionStreamWrapper>
        <Wrapper data-testid="newsletterPlacement">
          <SubscribeSection>
            <Title>{newsletterInfo.headline}</Title>
            <NewsletterButton
              ref={ref as React.RefObject<HTMLButtonElement | null>}
              onClick={onClick}
              text={type.notSubscribed.ctaLabel}
              successMessage={type.notSubscribed.confirmText}
              errorMessage={t(
                'Sorry, something went wrong! Please try again later.',
              )}
            />
          </SubscribeSection>
          <InfoSection>
            <Subtitle data-testid="USPTitle">
              {newsletterInfo.uspTitle}
            </Subtitle>
            <InfoItems>
              {newsletterInfo.usps.map((usp, index) => (
                <InfoItem key={index} data-testid="USPItem">
                  <StyledImage
                    src={usp.icon.url}
                    breakpoints={[]}
                    ratio={'1:1'}
                    alt={usp.icon.description}
                    widths={[50]}
                  />
                  <span>{usp.label}</span>
                </InfoItem>
              ))}
            </InfoItems>
          </InfoSection>
        </Wrapper>
      </NewsletterSectionStreamWrapper>
    );
  }

  return null;
};

const StyledImage = styled(Image)`
  width: 20px;
  height: 20px;
  flex-basis: 20px;
  ${desktopAndUpLinaria(
    `
        width: 24px;
        height: 24px;
        flex-basis: 24px;
    `,
  )}
`;

export const NewsletterSectionStreamWrapper = styled.div`
  padding: 1rem 2rem 2.5rem;
  background-color: ${COLORS.White};

  ${desktopAndUpLinaria(`padding: 0;`)};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 20px;

  ${desktopAndUpLinaria(
    `
      flex-direction: row;
      padding: 3rem 0;
    `,
  )};
`;

const SubscribeSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;

  ${desktopAndUpLinaria(
    `
      width: unset;
      flex: 0.5;
      gap: 24px;
    `,
  )}
`;

const Title = styled.span`
  font-size: ${FONT_SIZES.px20};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  text-align: center;

  ${desktopAndUpLinaria(
    `
      text-align: left;
      font-size: ${FONT_SIZES.px24};
    `,
  )}
`;

const InfoSection = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  height: fit-content;

  ${desktopAndUpLinaria(
    `
      width: unset;
      flex: 0.5;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      border-left: 1px solid ${COLORS.Grey3};
      padding-left: 20px;
    `,
  )}
`;

const InfoItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: ${FONT_SIZES.px14};
  flex: 0.5;

  ${desktopAndUpLinaria(
    `
      flex-direction: row;
      gap: 24px;
      font-size: ${FONT_SIZES.px20};
      font-weight: ${FONT_WEIGHTS.mediumHeavy};
    `,
  )};
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  > span {
    border-bottom: 4px solid ${COLORS.NeonYellow};
  }
`;

const Subtitle = styled.span`
  font-size: ${FONT_SIZES.px14};
  flex: 0.5;
  line-height: 1.5;

  ${desktopAndUpLinaria(
    `
      line-height: inherit;
      font-size: ${FONT_SIZES.px20};
      font-weight: ${FONT_WEIGHTS.mediumHeavy};
    `,
  )};
`;

const StyledInfoBox = styled(InfoBox)`
  margin: 10px auto 20px auto;
  font-size: ${FONT_SIZES.px10};

  ${desktopAndUpLinaria(`
    font-size: ${FONT_SIZES.px16};

    &&& {
      > svg {
        width: ${IconSize.M}px;
        height: ${IconSize.M}px;
        flex-basis: ${IconSize.M}px;
        margin-top: -2px;
      }
    }
  `)};
`;

const PendingInfoBox = styled(StyledInfoBox)`
  width: 100%;
`;

const SuccessInfoBox = styled(StyledInfoBox)`
  width: 90%;

  ${desktopAndUpLinaria(`
    width: 50%;

    &&& {
      > svg {
        width: ${IconSize.M}px;
        height: ${IconSize.M}px;
        flex-basis: ${IconSize.M}px;
        margin-top: -2px;
      }
    }
  `)};
`;
